/**
 * アカウント削除（退会）サービス。
 *
 * ymgFetch 経由で 2-step フローを実行する:
 *   1. POST /users/me/delete-request → `confirmationToken` を取得
 *   2. POST /users/me/delete-confirm → `gameId` + `confirmationToken` を body で送信
 *
 * 設計上の注意:
 *   - `ymgFetch()` を使うことで、Native session token の期限切れ時も
 *     `/auth/session/refresh` 経由の refresh / retry 経路を通す。
 *   - Web / LIFF 環境では呼び出し側で SettingsModal が `isCapacitorNative()` の場合のみ
 *     ボタンを出す設計なので、ここでも Native 以外は明示的に拒否する。
 *   - 削除成功後は Capacitor Preferences と localStorage を全クリアし、次回起動時に
 *     ゲストとして bootstrap される。
 *   - エラーはサイレントフォールバックせず、構造化された `DeleteResult` を返す。
 *
 * 関連ルール:
 *   - `.claude/rules/antipatterns.md` (サイレントフォールバック禁止)
 *   - `.claude/rules/no-hardcode.md` (エンドポイントは `config/auth.ts` に外出し)
 */
import {
  ACCOUNT_DELETE_ENDPOINTS,
  ACCOUNT_DELETE_ERRORS,
} from '../config/auth'
import { isCapacitorNative } from '../utils/platform'
import { getGameId, ymgFetch } from './sdk'

export type DeleteResultCode =
  (typeof ACCOUNT_DELETE_ERRORS)[keyof typeof ACCOUNT_DELETE_ERRORS]

export type DeleteResult =
  | { ok: true }
  /**
   * サーバー削除は成功したが、端末側のローカル状態クリアに失敗した。
   * UI は「サーバー側は削除済み、再起動して残データを消してください」と通知する。
   */
  | { ok: true; localClearFailed: true }
  | { ok: false; code: DeleteResultCode; httpStatus?: number; serverCode?: string }

interface DeleteRequestResponse {
  ok?: boolean
  confirmationToken?: string
  expiresAt?: string
}

/**
 * 退会フローを実行する。
 *
 * 戻り値の `ok: false` には次のコードが入りうる:
 *   - `NOT_AVAILABLE_ON_PLATFORM`: Web / LIFF からの呼び出し（仕様外）
 *   - `NO_SESSION`: SDK session が無い、または 401 が返った
 *   - `NO_CONFIRMATION_TOKEN`: Step 1 が成功したが token を返さない
 *   - `NETWORK_ERROR`: fetch そのものが throw（オフライン等）
 *   - `LOCAL_CLEAR_FAILED`: サーバー側削除は成功したがローカルクリアに失敗
 *   - `UNKNOWN_ERROR`: それ以外（HTTP 4xx/5xx も含む。`httpStatus` で判別可能）
 *
 * UI 側はこのコードを ACCOUNT_DELETE_UI のメッセージにマップして表示する。
 */
export async function deleteAccount(): Promise<DeleteResult> {
  if (!isCapacitorNative()) {
    return { ok: false, code: ACCOUNT_DELETE_ERRORS.notAvailable }
  }

  // --- Step 1: confirmationToken 取得 ---
  let confirmationToken: string
  try {
    const body = await ymgFetch<DeleteRequestResponse>(ACCOUNT_DELETE_ENDPOINTS.request, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      needsGameIdHeader: true,
      body: JSON.stringify({}),
    })
    if (!body.confirmationToken) {
      console.warn('[AccountDelete] delete-request returned no confirmationToken')
      return { ok: false, code: ACCOUNT_DELETE_ERRORS.noConfirmationToken }
    }
    confirmationToken = body.confirmationToken
  } catch (err) {
    console.warn('[AccountDelete] delete-request failed:', err)
    return describeSdkDeleteError(err)
  }

  // --- Step 2: 削除確定 ---
  try {
    await ymgFetch(ACCOUNT_DELETE_ENDPOINTS.confirm, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      needsGameIdHeader: true,
      body: JSON.stringify({
        gameId: getGameId(),
        confirmationToken,
      }),
    })
  } catch (err) {
    console.warn('[AccountDelete] delete-confirm failed:', err)
    return describeSdkDeleteError(err)
  }

  // --- Step 3: ローカル状態のクリア（Preferences + localStorage） ---
  // ここで失敗すると「サーバー側削除済み・ローカル残存」の半壊状態になるため、
  // 失敗を握り潰さず呼び出し元（UI）に伝える。
  const cleared = await clearLocalAccountData()
  if (!cleared) {
    return { ok: true, localClearFailed: true }
  }

  return { ok: true }
}

function describeSdkDeleteError(err: unknown): DeleteResult {
  const status = typeof err === 'object' && err !== null
    ? (err as { status?: unknown }).status
    : undefined
  const code = typeof err === 'object' && err !== null
    ? (err as { code?: unknown }).code
    : undefined
  const httpStatus = typeof status === 'number' ? status : undefined
  const serverCode = typeof code === 'string'
    ? code
    : httpStatus !== undefined
    ? `HTTP_${httpStatus}`
    : undefined

  if (httpStatus === 0 || serverCode === 'NETWORK_ERROR') {
    return { ok: false, code: ACCOUNT_DELETE_ERRORS.network, httpStatus, serverCode }
  }
  if (httpStatus === 401) {
    return { ok: false, code: ACCOUNT_DELETE_ERRORS.noSession, httpStatus, serverCode }
  }
  return {
    ok: false,
    code: ACCOUNT_DELETE_ERRORS.unknown,
    httpStatus,
    serverCode,
  }
}

/**
 * Preferences と localStorage を全クリアする。
 * 両方成功で true、いずれか失敗で false を返す（呼び出し元で UI 通知する）。
 */
async function clearLocalAccountData(): Promise<boolean> {
  let ok = true
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.clear()
  } catch (err) {
    console.warn('[AccountDelete] Preferences.clear failed:', err)
    ok = false
  }
  try {
    localStorage.clear()
  } catch (err) {
    console.warn('[AccountDelete] localStorage.clear failed:', err)
    ok = false
  }
  return ok
}
